// src/components/AuthorForm.js
import React, { useState, useEffect } from 'react';
import { TextField, Button, Container, Box, Typography } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { createAuthor, updateAuthor, getAuthorById } from '../services/authorService';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';

function AuthorForm() {
  const [initialValues, setInitialValues] = useState({
    name: '',
    email: '',
    bio: '',
    facebookLink: '',
    twitterLink: '',
    linkedinLink: '',
    instagramLink: '',
  });
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) fetchAuthor();
  }, [id]);

  const fetchAuthor = async () => {
    try {
      const data = await getAuthorById(id);
      setInitialValues({
        name: data.name,
        email: data.email,
        bio: data.bio,
        facebookLink: data.facebookLink,
        twitterLink: data.twitterLink,
        linkedinLink: data.linkedinLink,
        instagramLink: data.instagramLink,
      });
    } catch (error) {
      console.error('Error fetching author:', error);
    }
  };

  const AuthorSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    bio: Yup.string().max(300, 'Bio should not exceed 300 characters'),
    facebookLink: Yup.string().url('Invalid URL'),
    twitterLink: Yup.string().url('Invalid URL'),
    linkedinLink: Yup.string().url('Invalid URL'),
    instagramLink: Yup.string().url('Invalid URL'),
  });

  const handleSubmit = async (values) => {
    try {
      if (id) {
        await updateAuthor(id, values);
      } else {
        await createAuthor(values);
      }
      navigate('/authors');
    } catch (error) {
      console.error('Error saving author:', error);
      alert('Error saving author');
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="md">
        <Box mt={4}>
          <Typography variant="h4" gutterBottom>
            {id ? 'Edit Author' : 'Create Author'}
          </Typography>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={AuthorSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange }) => (
              <Form>
                <TextField
                  label="Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
                <TextField
                  label="Email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
                <TextField
                  label="Bio"
                  name="bio"
                  value={values.bio}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  multiline
                  rows={3}
                  error={touched.bio && Boolean(errors.bio)}
                  helperText={touched.bio && errors.bio}
                />
                <TextField
                  label="Facebook Link"
                  name="facebookLink"
                  value={values.facebookLink}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  error={touched.facebookLink && Boolean(errors.facebookLink)}
                  helperText={touched.facebookLink && errors.facebookLink}
                />
                <TextField
                  label="Twitter Link"
                  name="twitterLink"
                  value={values.twitterLink}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  error={touched.twitterLink && Boolean(errors.twitterLink)}
                  helperText={touched.twitterLink && errors.twitterLink}
                />
                <TextField
                  label="LinkedIn Link"
                  name="linkedinLink"
                  value={values.linkedinLink}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  error={touched.linkedinLink && Boolean(errors.linkedinLink)}
                  helperText={touched.linkedinLink && errors.linkedinLink}
                />
                <TextField
                  label="Instagram Link"
                  name="instagramLink"
                  value={values.instagramLink}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  error={touched.instagramLink && Boolean(errors.instagramLink)}
                  helperText={touched.instagramLink && errors.instagramLink}
                />
                <Box mt={4}>
                  <Button type="submit" variant="contained" color="primary" fullWidth>
                    {id ? 'Update Author' : 'Create Author'}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Container>
    </DashboardLayout>
  );
}

export default AuthorForm;
